import type { RouterHistory } from "@tanstack/history";
import { QueryClient } from "@tanstack/query-core";
import { html } from "lit";
import type { Router } from "../lib/router";
import { createLegacyRouterHistory } from "../lib/tsr/legacy-history";
import { LitRouter, createRoute } from "../lib/tsr/router";
import { type AppRouterContext, createAppRootRoute } from "./app-router-context";
import { createEmployeeRoute } from "./employees/[id]/employee.route";
import { createEmployeesAllRoute } from "./employees/all/employees-all.route";
import { createRosterRoute } from "./roster/roster.route";

export type { AppRouterContext } from "./app-router-context";

// Legacy URLs carry plain string params (`venue=3`, `entity-filters=[...]`);
// TanStack's default JSON search parsing would turn them into numbers/arrays.
const parseSearch = (search: string) => Object.fromEntries(new URLSearchParams(search));
const stringifySearch = (search: Record<string, unknown>) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
        if (value !== undefined) {
            params.set(key, String(value));
        }
    }
    const str = params.toString();
    return str ? `?${str}` : "";
};

function createRouteTree() {
    const rootRoute = createAppRootRoute();

    // Everything not yet migrated: matched, but rendered by the Routing mixin components.
    const legacyRoute = createRoute({ getParentRoute: () => rootRoute, path: "$" });

    return rootRoute.addChildren([
        createEmployeesAllRoute(rootRoute),
        createEmployeeRoute(rootRoute),
        createRosterRoute(rootRoute),
        legacyRoute,
    ]);
}

/**
 * One router per app instance and per embedded view, each with its own route
 * tree: router-core mutates routes when it builds its lookup tables.
 */
function createRouter(history: RouterHistory, context: AppRouterContext) {
    return new LitRouter({
        routeTree: createRouteTree(),
        history,
        context,
        parseSearch,
        stringifySearch,
        // Query owns freshness; the router always asks it.
        defaultPreloadStaleTime: 0,
        // Scrolling stays with the legacy router (hash only); router-core
        // would scroll the window, which is the partner's page when embedded.
        scrollRestoration: () => false,
        defaultPendingComponent: () =>
            html`<div class="fullbleed center-aligning center-justifying vertical layout">
                <ptc-ui-spinner active></ptc-ui-spinner>
            </div>`,
    });
}

export function createAppRouter({
    legacy,
    ...context
}: { legacy: Router } & Omit<AppRouterContext, "mode" | "embedded" | "reportDenied">) {
    return createRouter(createLegacyRouterHistory(legacy), {
        ...context,
        mode: "app",
        embedded: legacy.mode === "embedded",
        reportDenied: (path) => legacy.reportDeniedRoute(path),
    });
}

export function createViewRouter(history: RouterHistory, context: Omit<AppRouterContext, "mode" | "embedded">) {
    return createRouter(history, { ...context, mode: "view", embedded: true });
}

export function createQueryClient() {
    return new QueryClient();
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/router-core" {
    interface Register {
        router: AppRouter;
    }
}
